<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
// NOTICE: KAPTION-INTEGRATION - Removed AIAssistanceButton import (using Kaption AI features instead)
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
// NOTICE: KAPTION-INTEGRATION - Use KaptionCircleFlag for flag icons
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import { getLanguageName } from 'dashboard/constants/kaptionLanguages';
// NOTICE: KAPTION-INTEGRATION - Schedule popover for scheduled messages
import KaptionSchedulePopover from '../conversation/KaptionSchedulePopover.vue';
// NOTICE: KAPTION-INTEGRATION - Custom send icon from style guide
import KaptionSendIcon from 'dashboard/components/icons/KaptionSendIcon.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.1
import KaptionPhoneRequestAction from 'dashboard/components/kaption/KaptionPhoneRequestAction.vue';

// NOTICE: KAPTION-INTEGRATION - Language name mapping for utility template picker
const LANG_NAMES = {
  en: 'English',
  es: 'Español',
  pt_BR: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
};

export default {
  name: 'ReplyBottomPanel',
  // NOTICE: KAPTION-INTEGRATION - Removed AIAssistanceButton (using Kaption AI features instead)
  // NOTICE: KAPTION-INTEGRATION - Added KaptionCircleFlag for follow-up mode language picker
  // NOTICE: KAPTION-INTEGRATION - Added KaptionSchedulePopover for scheduled messages
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    Icon,
    KaptionCircleFlag,
    KaptionSchedulePopover,
    KaptionSendIcon,
    KaptionPhoneRequestAction,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    // NOTICE: KAPTION-INTEGRATION - Tooltip showing keyboard shortcut for send button
    sendButtonTooltip: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // NOTICE: KAPTION-INTEGRATION - Removed message prop (was only used for AIAssistanceButton)
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    // NOTICE: KAPTION-INTEGRATION - Flag to show template controls in follow-up mode
    isOnFollowupMode: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Flag to show template controls in schedule mode
    isOnScheduleMode: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Follow-up mode props for template controls
    followupLanguage: {
      type: String,
      default: 'en',
    },
    followupLanguages: {
      type: Array,
      default: () => ['en'],
    },
    canGoPrevTemplate: {
      type: Boolean,
      default: false,
    },
    canGoNextTemplate: {
      type: Boolean,
      default: false,
    },
    templateCounter: {
      type: String,
      default: '',
    },
    showTemplateSearch: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Attachment state for follow-up mode
    isUploadingAttachment: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - Schedule message props
    showScheduleButton: {
      type: Boolean,
      default: false,
    },
    messageType: {
      type: String,
      default: 'regular',
    },
    // NOTICE: KAPTION-INTEGRATION - WhatsApp formatting toggle props
    showWhatsappFormattingToggle: {
      type: Boolean,
      default: false,
    },
    isWhatsappFormattingEnabled: {
      type: Boolean,
      default: false,
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.1
    phoneRequestFeatureEnabled: {
      type: Boolean,
      default: false,
    },
    phoneRequestPhoneNumber: {
      type: String,
      default: '',
    },
    phoneRequestHasBsuidAlias: {
      type: Boolean,
      default: false,
    },
    phoneRequestOutboundRecipientEnabled: {
      type: Boolean,
      default: false,
    },
    phoneRequestCanSendMessages: {
      type: Boolean,
      default: false,
    },
    phoneRequestCanReply: {
      type: Boolean,
      default: false,
    },
    onPhoneRequestSend: {
      type: Function,
      default: () => Promise.resolve(),
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'toggleEditor',
    'selectWhatsappTemplate',
    'openCannedResponseBrowser',
    'selectContentTemplate',
    // NOTICE: KAPTION-INTEGRATION - Follow-up mode events
    'changeFollowupLanguage',
    'prevTemplate',
    'nextTemplate',
    'toggleTemplateSearch',
    // NOTICE: KAPTION-INTEGRATION - Schedule message event
    'scheduleMessage',
    // NOTICE: KAPTION-INTEGRATION - Attachment event for follow-up mode
    'triggerAttachment',
    // NOTICE: KAPTION-INTEGRATION - WhatsApp formatting toggle event
    'toggleWhatsappFormatting',
    // NOTICE: KAPTION-INTEGRATION - [@] mention insert event (Notes mode)
    'insertMention',
  ],
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();
    // NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
    const { canUseFeature } = useFeaturePermissions();

    const uploadRef = ref(false);
    const showLanguageDropdown = ref(false);
    // NOTICE: KAPTION-INTEGRATION - Schedule popover state
    const showSchedulePopover = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      // NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
      canUseFeature,
      uploadRef,
      showLanguageDropdown,
      // NOTICE: KAPTION-INTEGRATION - Schedule popover state
      showSchedulePopover,
      LANG_NAMES,
      getLanguageName,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
    };
  },
  computed: {
    // NOTICE: KAPTION-INTEGRATION - Removed uiFlags getter (was only used for AIAssistanceButton)
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      // feature-permissions.REPLY_BOX.4
      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        this.canUseFeature('voice_recording')
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    // feature-permissions.REPLY_BOX.5
    showMessageSignatureButton() {
      return !this.isOnPrivateNote && this.canUseFeature('agent_signature');
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    // NOTICE: KAPTION-INTEGRATION - Removed isFetchingAppIntegrations (was only used for AIAssistanceButton)
    // NOTICE: KAPTION-INTEGRATION - Normalize language code for flag (pt_BR -> pt)
    normalizedFollowupLanguage() {
      // Convert pt_BR to pt for flag lookup
      return this.followupLanguage?.replace('_BR', '') || 'en';
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    // NOTICE: KAPTION-INTEGRATION - Follow-up mode methods
    normalizeLanguageCode(lang) {
      // Convert pt_BR to pt for flag lookup
      return lang?.replace('_BR', '') || 'en';
    },
    selectLanguage(lang) {
      this.$emit('changeFollowupLanguage', lang);
      this.showLanguageDropdown = false;
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    closeLanguageDropdown() {
      this.showLanguageDropdown = false;
    },
    // NOTICE: KAPTION-INTEGRATION - Schedule popover methods
    toggleSchedulePopover() {
      this.showSchedulePopover = !this.showSchedulePopover;
    },
    closeSchedulePopover() {
      this.showSchedulePopover = false;
    },
    handleScheduleMessage(scheduleData) {
      this.$emit('scheduleMessage', scheduleData);
      this.showSchedulePopover = false;
    },
  },
};
</script>

<template>
  <div class="flex justify-between p-3" :class="wrapClass">
    <!-- NOTICE: KAPTION-INTEGRATION - Show template controls in follow-up or schedule mode -->
    <div class="left-wrap">
      <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.1 -->
      <KaptionPhoneRequestAction
        :channel-type="channelType"
        :provider="inbox.provider"
        :phone-number="phoneRequestPhoneNumber"
        :has-bsuid-alias="phoneRequestHasBsuidAlias"
        :outbound-recipient-enabled="phoneRequestOutboundRecipientEnabled"
        :can-send-messages="phoneRequestCanSendMessages"
        :can-reply="phoneRequestCanReply"
        :feature-enabled="phoneRequestFeatureEnabled"
        :on-send="onPhoneRequestSend"
      />
      <template v-if="isOnFollowupMode || isOnScheduleMode">
        <!-- Language Flag Picker -->
        <div class="relative">
          <button
            class="flag-btn"
            :title="
              LANG_NAMES[followupLanguage] ||
              getLanguageName(normalizedFollowupLanguage)
            "
            @click="toggleLanguageDropdown"
          >
            <KaptionCircleFlag
              :language-code="normalizedFollowupLanguage"
              size="size-5"
            />
            <Icon icon="i-lucide-chevron-down" class="size-3 ml-0.5" />
          </button>
          <!-- Language Dropdown -->
          <div
            v-if="showLanguageDropdown"
            v-on-clickaway="closeLanguageDropdown"
            class="language-dropdown"
          >
            <button
              v-for="lang in followupLanguages"
              :key="lang"
              class="lang-option"
              :class="{ 'is-selected': lang === followupLanguage }"
              @click="selectLanguage(lang)"
            >
              <KaptionCircleFlag
                :language-code="normalizeLanguageCode(lang)"
                size="size-5"
              />
              <span class="lang-name">{{
                LANG_NAMES[lang] || getLanguageName(normalizeLanguageCode(lang))
              }}</span>
            </button>
          </div>
        </div>

        <!-- Search Toggle -->
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_SEARCH_TEMPLATES')"
          icon="i-lucide-search"
          slate
          faded
          sm
          :class="{ 'search-active': showTemplateSearch }"
          @click="$emit('toggleTemplateSearch')"
        />

        <!-- Template Navigation -->
        <div class="template-nav">
          <NextButton
            icon="i-lucide-chevron-left"
            slate
            faded
            xs
            :disabled="!canGoPrevTemplate"
            @click="$emit('prevTemplate')"
          />
          <span class="template-counter">{{ templateCounter }}</span>
          <NextButton
            icon="i-lucide-chevron-right"
            slate
            faded
            xs
            :disabled="!canGoNextTemplate"
            @click="$emit('nextTemplate')"
          />
        </div>

        <!-- NOTICE: KAPTION-INTEGRATION - Attachment button for follow-up/schedule mode -->
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          :icon="isUploadingAttachment ? 'i-lucide-loader-2' : 'i-ph-paperclip'"
          slate
          faded
          sm
          :disabled="isUploadingAttachment"
          :class="{ 'animate-spin': isUploadingAttachment }"
          @click="$emit('triggerAttachment')"
        />
      </template>
      <template v-else>
        <!-- NOTICE: KAPTION-INTEGRATION - [@] mention icon, Notes mode only -->
        <NextButton
          v-if="isNote"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_MENTION_ICON')"
          icon="i-lucide-at-sign"
          slate
          faded
          sm
          data-testid="insert-mention-button"
          @click="$emit('insertMention')"
        />
        <!-- feature-permissions.REPLY_BOX.7 -->
        <NextButton
          v-if="canUseFeature('emoji_picker')"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley-sticker"
          slate
          faded
          sm
          @click="toggleEmojiPicker"
        />
        <FileUpload
          ref="uploadRef"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          input-id="conversationAttachment"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="enableDragAndDrop"
          :drop-directory="false"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <NextButton
            v-if="showAttachButton"
            v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="i-ph-paperclip"
            slate
            faded
            sm
          />
        </FileUpload>
        <NextButton
          v-if="showAudioRecorderButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
          :icon="
            !isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'
          "
          slate
          faded
          sm
          @click="toggleAudioRecorder"
        />
        <NextButton
          v-if="showEditorToggle"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
          icon="i-ph-quotes"
          slate
          faded
          sm
          @click="$emit('toggleEditor')"
        />
        <NextButton
          v-if="showAudioPlayStopButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
          :icon="audioRecorderPlayStopIcon"
          slate
          faded
          sm
          :label="recordingAudioDurationText"
          @click="toggleAudioRecorderPlayPause"
        />
        <NextButton
          v-if="showMessageSignatureButton"
          v-tooltip.top-end="signatureToggleTooltip"
          icon="i-ph-signature"
          slate
          faded
          sm
          @click="toggleMessageSignature"
        />
        <!-- feature-permissions.REPLY_BOX.2 -->
        <NextButton
          v-if="canUseFeature('canned_responses')"
          v-tooltip.top-end="$t('CANNED_RESPONSE_BROWSER.BUTTON_TOOLTIP')"
          icon="i-ph-lightning"
          slate
          faded
          sm
          @click="$emit('openCannedResponseBrowser')"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - WhatsApp formatting toggle button -->
        <NextButton
          v-if="showWhatsappFormattingToggle"
          v-tooltip.top-end="
            isWhatsappFormattingEnabled
              ? $t('CONVERSATION.REPLYBOX.TIP_WHATSAPP_FORMATTING_DISABLE')
              : $t('CONVERSATION.REPLYBOX.TIP_WHATSAPP_FORMATTING_ENABLE')
          "
          icon="i-lucide-bold"
          slate
          faded
          sm
          :class="{ 'whatsapp-formatting-active': isWhatsappFormattingEnabled }"
          @click="$emit('toggleWhatsappFormatting')"
        />
        <NextButton
          v-if="enableWhatsAppTemplates"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableContentTemplates"
          v-tooltip.top-end="'Content Templates'"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectContentTemplate')"
        />
        <VideoCallButton
          v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
          :conversation-id="conversationId"
        />
        <!-- NOTICE: KAPTION-INTEGRATION - Removed AIAssistanceButton (using Kaption AI features instead) -->
        <transition name="modal-fade">
          <div
            v-show="uploadRef && uploadRef.dropActive"
            class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
          >
            <fluent-icon icon="cloud-backup" size="40" />
            <h4 class="text-2xl break-words text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
            </h4>
          </div>
        </transition>
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="
            $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          "
          icon="i-ph-article-ny-times"
          slate
          faded
          sm
          @click="toggleInsertArticle"
        />
      </template>
    </div>
    <div class="right-wrap">
      <!-- NOTICE: KAPTION-INTEGRATION - Schedule button with popover (for non-Schedule mode) -->
      <div
        v-if="showScheduleButton && !isNote && !isOnScheduleMode"
        class="relative"
      >
        <NextButton
          v-tooltip.top="
            $t(
              'CONVERSATION.SCHEDULE_MESSAGE.BUTTON_TOOLTIP',
              'Schedule message'
            )
          "
          icon="i-lucide-clock"
          slate
          faded
          sm
          :disabled="isSendDisabled"
          class="flex-shrink-0 mr-2"
          @click="toggleSchedulePopover"
        />
        <KaptionSchedulePopover
          :show="showSchedulePopover"
          :message-type="messageType"
          :disabled="isSendDisabled"
          @close="closeSchedulePopover"
          @schedule="handleScheduleMessage"
        />
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION - In Schedule mode, the main button opens the schedule popover -->
      <div v-if="isOnScheduleMode" class="relative">
        <NextButton
          :label="sendButtonText"
          :title="sendButtonTooltip"
          trailing-icon
          type="submit"
          sm
          color="blue"
          :disabled="isSendDisabled"
          class="flex-shrink-0"
          @click="toggleSchedulePopover"
        >
          <template #icon>
            <KaptionSendIcon :size="16" />
          </template>
        </NextButton>
        <KaptionSchedulePopover
          :show="showSchedulePopover"
          :message-type="messageType"
          :disabled="isSendDisabled"
          @close="closeSchedulePopover"
          @schedule="handleScheduleMessage"
        />
      </div>
      <!-- NOTICE: KAPTION-INTEGRATION - Regular send button (for non-Schedule mode) -->
      <NextButton
        v-if="!isOnScheduleMode"
        :label="sendButtonText"
        :title="sendButtonTooltip"
        trailing-icon
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      >
        <template #icon>
          <KaptionSendIcon :size="16" />
        </template>
      </NextButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  @apply flex;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}

/* NOTICE: KAPTION-INTEGRATION - Follow-up mode styles */
.flag-btn {
  @apply flex items-center gap-0.5 px-2 py-1.5 rounded-lg;
  @apply bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors;
  @apply border border-n-weak;
}

.language-dropdown {
  @apply absolute bottom-full left-0 mb-2 z-50;
  @apply bg-n-solid-2 border border-n-weak rounded-lg shadow-lg;
  @apply py-1 min-w-[140px];
}

.lang-option {
  @apply flex items-center gap-2 w-full px-3 py-2;
  @apply hover:bg-n-alpha-2 transition-colors text-left;

  &.is-selected {
    @apply bg-n-blue-3;
  }
}

.lang-name {
  @apply text-sm text-n-slate-12;
}

.template-nav {
  @apply flex items-center gap-1;
}

.template-counter {
  @apply text-xs text-n-slate-10 font-mono px-1 min-w-[2.5rem] text-center;
}

.search-active {
  @apply bg-n-blue-3;
}

/* NOTICE: KAPTION-INTEGRATION - WhatsApp formatting toggle active state */
.whatsapp-formatting-active {
  @apply bg-n-green-3 text-n-green-11;
}
</style>
